import { Link } from "react-router-dom";
import { AlertTriangle, MapPin, ShieldCheck, Star, Send, Phone } from "lucide-react";
import { hvacPath } from "../../seo/hosts";
import {
  LEES_CONTACT, LEES_HOURS, LEES_PAYMENT_METHODS, LEES_SERVICE_AREAS, LEES_PRICING,
  LEES_PRICING_FACTORS, LEES_PROCESS_STEPS, LEES_CREDENTIALS, LEES_WORKMANSHIP_STATEMENT, LEES_SOCIAL,
} from "../../data/leesContent";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const RED_BTN = "inline-flex items-center gap-2 bg-[#8B1E1E] hover:bg-[#A6271F] text-white font-bold px-6 py-3 rounded-sm uppercase tracking-wide text-sm transition-colors";

// Light/dark section wrapper — alternating bands keep the existing Lee's look.
export function Band({ light, tint, id, testid, wide, children }) {
  const tone = light ? `${tint ? "bg-slate-50" : "bg-white"} text-[#111111]` : "bg-[#111111] text-white";
  return (
    <section id={id} className={`${tone} py-14 md:py-20 border-b-2 border-[#8B1E1E] scroll-mt-28`} data-testid={testid}>
      <div className={`${wide ? "max-w-6xl" : "max-w-5xl"} mx-auto px-5`}>{children}</div>
    </section>
  );
}

export function SectionHead({ eyebrow, title, intro, light, as: H = "h2" }) {
  return (
    <div className="text-center mb-10">
      <p className={`hvac-stencil ${light ? "text-[#8B1E1E]" : "text-[#EF4444]"} text-xs font-bold uppercase tracking-[0.3em] mb-2`}>{eyebrow}</p>
      <H className="hvac-stencil text-3xl md:text-4xl font-bold uppercase leading-tight">{title}</H>
      {intro && <p className={`mt-3 text-sm md:text-base max-w-2xl mx-auto leading-relaxed ${light ? "text-slate-600" : "text-slate-400"}`}>{intro}</p>}
    </div>
  );
}

export function PageHero({ eyebrow, title, intro, cta = "Request Service" }) {
  return (
    <section className="hvac-grain border-b-2 border-[#8B1E1E]" data-testid="hvac-page-hero">
      <div className="max-w-5xl mx-auto px-5 py-14 md:py-20">
        <p className="inline-block hvac-stencil text-[#EF4444] text-xs font-bold uppercase tracking-[0.25em] border border-[#8B1E1E] px-3 py-1.5 mb-5">{eyebrow}</p>
        <h1 className="hvac-stencil hvac-stamp text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[1.05]">{title}</h1>
        <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl">{intro}</p>
        <div className="mt-7 flex flex-wrap gap-3">
          <a href="#hvac-quote" className={RED_BTN} data-testid="hvac-page-hero-cta"><Send size={16} /> {cta}</a>
          <a href={LEES_CONTACT.phoneHref} className="inline-flex items-center gap-2 border-2 border-white hover:bg-white/10 text-white font-bold px-6 py-3 rounded-sm uppercase tracking-wide text-sm transition-colors" data-testid="hvac-page-hero-call">
            <Phone size={16} /> Call {LEES_CONTACT.phone}
          </a>
        </div>
      </div>
    </section>
  );
}

export function MoreLink({ to, children, testid }) {
  return (
    <div className="mt-8 text-center">
      <Link to={hvacPath(to)} className={RED_BTN} data-testid={testid}>{children} →</Link>
    </div>
  );
}

export function PricingGrid({ items = LEES_PRICING }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {items.map((p) => (
        <article key={p.label} className="border border-slate-200 rounded-sm p-5" data-testid={`hvac-price-${slug(p.label)}`}>
          <h3 className="hvac-stencil text-sm md:text-base font-bold uppercase tracking-wide text-slate-900">{p.label}</h3>
          <p className="mt-1 text-[#8B1E1E] font-bold text-base">{p.price}</p>
          {p.note && <p className="mt-1 text-xs md:text-sm text-slate-500 leading-relaxed">{p.note}</p>}
        </article>
      ))}
    </div>
  );
}

export function PricingFactors() {
  return (
    <div className="mt-8 bg-slate-50 border border-slate-200 rounded-sm p-5">
      <p className="hvac-stencil text-[#8B1E1E] text-xs font-bold uppercase tracking-[0.25em] mb-2">Pricing can vary with</p>
      <div className="flex flex-wrap gap-2">
        {LEES_PRICING_FACTORS.map((f) => <span key={f} className="text-xs md:text-sm text-slate-700 bg-white border border-slate-200 px-3 py-1.5 rounded-sm">{f}</span>)}
      </div>
    </div>
  );
}

export function StepList({ steps = LEES_PROCESS_STEPS, light, cols = "lg:grid-cols-4" }) {
  return (
    <ol className={`grid grid-cols-1 md:grid-cols-2 ${cols} gap-3`}>
      {steps.map((s, i) => (
        <li key={s.title} className={light ? "border border-slate-200 rounded-sm p-5" : "hvac-card p-5"}>
          <div className={`hvac-stencil ${light ? "text-[#8B1E1E]" : "text-[#EF4444]"} text-xs font-bold uppercase tracking-[0.25em] mb-2`}>Step · {String(i + 1).padStart(2, "0")}</div>
          <h3 className="hvac-stencil text-base font-bold uppercase mb-2">{s.title}</h3>
          <p className={`${light ? "text-slate-600" : "text-slate-400"} text-sm leading-relaxed`}>{s.text}</p>
        </li>
      ))}
    </ol>
  );
}

export function FaqGroups({ groups, testid = "hvac-faq-list", level = 3 }) {
  const GroupH = `h${level}`;
  const ItemH = `h${level + 1}`;
  return (
    <div data-testid={testid}>
      {groups.map((g) => (
        <div key={g.key} id={`faq-${g.key}`} className="mb-8 scroll-mt-28">
          <GroupH className="hvac-stencil text-lg md:text-xl font-bold uppercase text-[#8B1E1E] mb-3">{g.title}</GroupH>
          <div className="divide-y divide-slate-200 border-y border-slate-200">
            {g.items.map((it) => (
              <details key={it.q} className="group py-4" data-testid={`hvac-faq-${slug(it.q).slice(0, 40)}`}>
                <summary className="flex items-start justify-between cursor-pointer list-none">
                  <ItemH className="font-semibold text-base pr-6 text-slate-900">{it.q}</ItemH>
                  <span className="text-[#8B1E1E] font-bold text-lg leading-none mt-1 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-slate-700 text-sm md:text-base leading-relaxed">{it.a}</p>
              </details>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ReviewCards({ reviews }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {reviews.map((r) => (
        <article key={r.name} className="hvac-card p-6" data-testid={`hvac-review-${slug(r.name)}`}>
          <div className="flex items-center gap-1 text-[#F59E0B] mb-2" role="img" aria-label={`${r.rating} out of 5 stars`}>
            {Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={14} fill="#F59E0B" strokeWidth={0} aria-hidden="true" />)}
          </div>
          <p className="text-slate-200 text-sm md:text-base leading-relaxed">{r.summary}</p>
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <span className="hvac-stencil text-[#F59E0B] text-xs font-bold uppercase tracking-wide">— {r.name}</span>
            <a href={r.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-400 hover:text-white underline">{r.source}</a>
          </div>
        </article>
      ))}
    </div>
  );
}

export function ProfileLinks({ keys, light, verb = "Read more on" }) {
  const links = LEES_SOCIAL.filter((s) => s.url && (!keys || keys.includes(s.key)));
  return (
    <div className="mt-10 flex flex-wrap justify-center gap-3">
      {links.map((s) => (
        <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 font-bold px-5 py-2.5 rounded-sm text-sm transition-colors ${light ? "border border-slate-300 text-slate-800 hover:bg-slate-50" : "bg-white text-[#111] hover:bg-slate-100"}`} data-testid={`hvac-profile-${s.key}`}>
          <Star size={14} className="text-[#F59E0B]" /> {verb} {s.label}
        </a>
      ))}
    </div>
  );
}

export function EmergencySteps() {
  return (
    <ol className="space-y-3 text-slate-200 text-sm md:text-base list-decimal pl-6" data-testid="hvac-emergency-steps">
      <li>Submit the service request on this page.</li>
      <li>Mark the request as an <strong className="text-[#FFB4B4]">emergency</strong>.</li>
      <li>Call or text <a className="underline text-[#FFB4B4]" href={LEES_CONTACT.phoneHref}>{LEES_CONTACT.phone}</a> after submitting so we know the request is waiting.</li>
      <li>Email <a className="underline text-[#FFB4B4] break-all" href={LEES_CONTACT.emailHref}>{LEES_CONTACT.email}</a> as backup if needed.</li>
    </ol>
  );
}

export function SafetyBox() {
  return (
    <div className="mt-7 bg-[#1a0a0a] border border-[#8B1E1E]/80 p-5 rounded-sm" data-testid="hvac-safety-box">
      <h3 className="hvac-stencil text-sm md:text-base font-bold uppercase text-[#FFB4B4] mb-2 flex items-center gap-2"><AlertTriangle size={16} /> Safety First</h3>
      <p className="text-slate-300 text-sm md:text-base leading-relaxed">If there is a suspected natural-gas leak, a carbon-monoxide alarm or exposure, smoke/fire, or active serious electrical burning or arcing — leave the area and contact 911, the fire department or your utility <strong>before</strong> waiting for an HVAC appointment.</p>
    </div>
  );
}

export function HoursList() {
  return (
    <>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-slate-200 text-sm md:text-base" data-testid="hvac-hours">
        {LEES_HOURS.map((h) => (
          <li key={h.label} className="hvac-card p-4 flex items-start justify-between gap-4">
            <span className="hvac-stencil uppercase tracking-wide text-[#F59E0B]">{h.label}</span>
            <span className="text-right">{h.value}</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-slate-400 text-center mt-4">Emergency response outside normal hours is not guaranteed until you reach us and we confirm availability.</p>
    </>
  );
}

export function ServiceAreaTiers() {
  return (
    <div data-testid="hvac-service-area-tiers">
      <h3 className="hvac-stencil text-sm uppercase tracking-[0.25em] text-[#8B1E1E] mb-3">Core service area</h3>
      <div className="flex flex-wrap gap-2 mb-7">
        {LEES_SERVICE_AREAS.core.map((c) => (
          <span key={c} className="inline-flex items-center gap-2 border border-[#8B1E1E] text-sm text-slate-800 px-3 py-1.5 hvac-stencil uppercase tracking-wide" data-testid={`core-area-${slug(c)}`}>
            <MapPin size={12} className="text-[#8B1E1E]" /> {c}
          </span>
        ))}
      </div>
      <h3 className="hvac-stencil text-sm uppercase tracking-[0.25em] text-slate-600 mb-3">Strong surrounding areas</h3>
      <div className="flex flex-wrap gap-2 mb-5">
        {LEES_SERVICE_AREAS.surrounding.map((c) => (
          <span key={c} className="inline-flex items-center gap-2 border border-slate-300 text-sm text-slate-700 px-3 py-1.5"><MapPin size={12} className="text-slate-400" /> {c}</span>
        ))}
      </div>
      <p className="text-sm text-slate-500 italic">Also serving surrounding Northwest Indiana communities. Nearby Illinois service available by request, particularly communities near the Indiana border — additional travel charges may apply.</p>
    </div>
  );
}

export function CredentialsList() {
  return (
    <>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="hvac-credentials">
        {LEES_CREDENTIALS.map((c) => (
          <li key={c} className="hvac-card p-4 text-slate-200 text-sm md:text-base flex items-start gap-3"><ShieldCheck size={16} className="text-[#F59E0B] mt-1 flex-shrink-0" /><span>{c}</span></li>
        ))}
      </ul>
      <p className="text-xs text-slate-400 text-center mt-4 italic">Individual credentials vary by technician. Lee&apos;s obtains and documents the appropriate credentials for the work performed.</p>
    </>
  );
}

const POLICIES = [
  { title: "Workmanship Policy", body: LEES_WORKMANSHIP_STATEMENT },
  { title: "Parts & Authorization", list: [
    "We normally source replacement components through professional HVAC supply houses.",
    "Customer approval is obtained before purchasing expensive or special-order parts.",
    "Deposits may be required for special-order parts.",
    "Customer-supplied components may be declined.",
    "Where an eligible manufacturer warranty exists, we can provide the applicable product paperwork.",
  ] },
  { title: "Cancellation / No-Show", body: "Once a service visit has been approved and a technician has begun traveling or committed time to the appointment, agreed travel/service charges may still apply if access is unavailable or the appointment is cancelled without sufficient notice." },
  { title: "Pet & Home Access", body: "Please secure pets during the service visit so technicians can safely move equipment and focus on the repair. Lee's is an animal-loving family business — securing pets protects the customer, pets and technicians." },
];

export function PoliciesGrid({ only }) {
  const items = only ? POLICIES.filter((p) => only.includes(p.title)) : POLICIES;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="hvac-policies">
      {items.map((p) => (
        <article key={p.title} className="border border-slate-200 rounded-sm p-6">
          <h3 className="hvac-stencil text-lg font-bold uppercase mb-3">{p.title}</h3>
          {p.body && <p className="text-slate-700 text-sm md:text-base leading-relaxed">{p.body}</p>}
          {p.list && <ul className="text-slate-700 text-sm md:text-base leading-relaxed space-y-2 list-disc pl-5">{p.list.map((l) => <li key={l}>{l}</li>)}</ul>}
        </article>
      ))}
    </div>
  );
}

export function PaymentMethods() {
  return (
    <div className="text-center" data-testid="hvac-payments">
      <ul className="flex flex-wrap justify-center gap-2 mb-4">
        {LEES_PAYMENT_METHODS.map((m) => <li key={m} className="text-xs md:text-sm text-slate-700 border border-slate-200 bg-slate-50 px-3 py-1.5 rounded-sm">{m}</li>)}
      </ul>
      <p className="text-xs text-slate-500 italic">Card or electronic payment processing fees may apply where permitted and will be disclosed before payment.</p>
    </div>
  );
}

export function RedCTA({ eyebrow, title, text, label = "Request Service", testid }) {
  return (
    <section className="bg-[#8B1E1E] text-white py-12 md:py-14" data-testid={testid}>
      <div className="max-w-4xl mx-auto px-5 text-center">
        {eyebrow && <p className="hvac-stencil text-white/80 text-xs font-bold uppercase tracking-[0.3em] mb-2">{eyebrow}</p>}
        <h2 className="hvac-stencil text-3xl md:text-4xl font-bold uppercase">{title}</h2>
        {text && <p className="mt-3 text-white/90 text-base md:text-lg">{text}</p>}
        <a href="#hvac-quote" className="inline-flex items-center gap-2 mt-6 bg-white text-[#8B1E1E] font-bold px-6 py-3 rounded-sm uppercase tracking-wide text-sm hover:bg-slate-100 transition-colors" data-testid={testid ? `${testid}-btn` : undefined}>
          {label} →
        </a>
      </div>
    </section>
  );
}
